// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { MasterRider } from './MasterRider';
import { TeamId } from './TeamId';
import { SkillId } from './SkillId';
import { VehicleCostModel } from './VehicleCostModel';
import { RiderId } from './RiderId';
import { Shift } from './Shift';
import { ResourceLimit } from './ResourceLimit';
import { AuditMetadata } from './AuditMetadata';
import { Document } from './Document';
import { MasterVehicleModel } from './MasterVehicleModel';
import { VehicleModelId } from './VehicleModelId';
import { BaseLocations } from './BaseLocations';
import { LDate } from 'common/models/flow/LDate';

import type { TeamIdModelType } from './TeamId';
import type { SkillIdModelType } from './SkillId';
import type { VehicleCostModelModelType } from './VehicleCostModel';
import type { RiderIdModelType } from './RiderId';
import type { ShiftModelType } from './Shift';
import type { ResourceLimitModelType } from './ResourceLimit';
import type { AuditMetadataModelType } from './AuditMetadata';
import type { DocumentModelType } from './Document';
import type { MasterVehicleModelModelType } from './MasterVehicleModel';
import type { VehicleModelIdModelType } from './VehicleModelId';
import type { BaseLocationsModelType } from './BaseLocations';
import type { LDateModelType } from 'common/models/flow/LDate';
import type { MasterRiderModelType } from './MasterRider';

export type FuelTypeType = 'PETROL' | 'DIESEL';
export type FleetTypeEnum = 'CAPTIVE' | 'THIRD_PARTY' | 'ADHOC';

export type MasterVehicleModelType = {
    clientId: string,
    id: string,
    name: string,
    code: string,
    status: string,
    version: number,
    riderId: ?RiderIdModelType,
    rider: ?MasterRiderModelType,
    vehicleCostModel: VehicleCostModelModelType,
    teams: Array<TeamIdModelType>,
    skills: Array<SkillIdModelType>,
    shifts: Array<ShiftModelType>,
    resourceLimits: Array<ResourceLimitModelType>,
    manufacturingDate: ?LDateModelType,
    isColdChain: boolean,
    unladenWeight: number,
    fuelType: FuelTypeType,
    mileage: number,
    userCount: number,
    documents: Array<DocumentModelType>,
    customProperties: { [key: string]: string },
    auditMetadata: ?AuditMetadataModelType,
    vehicleModel: ?MasterVehicleModelModelType,
    vehicleModelId: ?VehicleModelIdModelType,
    registrationNumber: string,
    isActive: boolean,
    baseLocations: ?BaseLocationsModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterVehicle extends ImmutableModel {
    static fromJS(json: MasterVehicleModelType): MasterVehicle {
        const state: Object = Object.assign({}, json);
        state.riderId = state.riderId
            ? RiderId.fromJS(state.riderId)
            : state.riderId;
        state.rider = state.rider
            ? MasterRider.fromJS(state.rider)
            : state.rider;
        state.vehicleCostModel = VehicleCostModel.fromJS(
            state.vehicleCostModel
        );
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.shifts = Immutable.List(state.shifts).map(item =>
            Shift.fromJS(item)
        );
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        state.manufacturingDate = state.manufacturingDate
            ? LDate.fromJS(state.manufacturingDate)
            : state.manufacturingDate;
        state.documents = Immutable.List(state.documents).map(item =>
            Document.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.vehicleModel = state.vehicleModel
            ? MasterVehicleModel.fromJS(state.vehicleModel)
            : state.vehicleModel;
        state.vehicleModelId = state.vehicleModelId
            ? VehicleModelId.fromJS(state.vehicleModelId)
            : state.vehicleModelId;
        state.baseLocations = state.baseLocations
            ? BaseLocations.fromJS(state.baseLocations)
            : state.baseLocations;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterVehicleModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            code: this.code,
            status: this.status,
            version: this.version,
            riderId: this.riderId ? this.riderId.toJS() : this.riderId,
            rider: this.rider ? this.rider.toJS() : this.rider,
            vehicleCostModel: this.vehicleCostModel.toJS(),
            teams: this.teams.toArray().map(item => item.toJS()),
            skills: this.skills.toArray().map(item => item.toJS()),
            shifts: this.shifts.toArray().map(item => item.toJS()),
            resourceLimits: this.resourceLimits
                .toArray()
                .map(item => item.toJS()),
            manufacturingDate: this.manufacturingDate
                ? this.manufacturingDate.toJS()
                : this.manufacturingDate,
            isColdChain: this.isColdChain,
            unladenWeight: this.unladenWeight,
            fuelType: this.fuelType,
            mileage: this.mileage,
            userCount: this.userCount,
            documents: this.documents.toArray().map(item => item.toJS()),
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            vehicleModel: this.vehicleModel
                ? this.vehicleModel.toJS()
                : this.vehicleModel,
            vehicleModelId: this.vehicleModelId
                ? this.vehicleModelId.toJS()
                : this.vehicleModelId,
            registrationNumber: this.registrationNumber,
            isActive: this.isActive,
            baseLocations: this.baseLocations
                ? this.baseLocations.toJS()
                : this.baseLocations
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get status(): string {
        return this._state.get('status');
    }

    setStatus(status: string): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get riderId(): ?RiderId {
        return this._state.get('riderId');
    }

    setRiderId(riderId: ?RiderId): this {
        return this.clone(this._state.set('riderId', riderId));
    }

    get rider(): ?MasterRider {
        return this._state.get('rider');
    }

    setRider(rider: ?MasterRider): this {
        return this.clone(this._state.set('rider', rider));
    }

    get vehicleCostModel(): VehicleCostModel {
        return this._state.get('vehicleCostModel');
    }

    setVehicleCostModel(vehicleCostModel: VehicleCostModel): this {
        return this.clone(
            this._state.set('vehicleCostModel', vehicleCostModel)
        );
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    setSkills(skills: Immutable.List<SkillId>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get shifts(): Immutable.List<Shift> {
        return this._state.get('shifts');
    }

    setShifts(shifts: Immutable.List<Shift>): this {
        return this.clone(this._state.set('shifts', shifts));
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    setResourceLimits(resourceLimits: Immutable.List<ResourceLimit>): this {
        return this.clone(this._state.set('resourceLimits', resourceLimits));
    }

    get manufacturingDate(): ?LDate {
        return this._state.get('manufacturingDate');
    }

    setManufacturingDate(manufacturingDate: ?LDate): this {
        return this.clone(
            this._state.set('manufacturingDate', manufacturingDate)
        );
    }

    get isColdChain(): boolean {
        return this._state.get('isColdChain');
    }

    setIsColdChain(isColdChain: boolean): this {
        return this.clone(this._state.set('isColdChain', isColdChain));
    }

    get unladenWeight(): number {
        return this._state.get('unladenWeight');
    }

    setUnladenWeight(unladenWeight: number): this {
        return this.clone(this._state.set('unladenWeight', unladenWeight));
    }

    get fuelType(): FuelTypeType {
        return this._state.get('fuelType');
    }

    setFuelType(fuelType: FuelTypeType): this {
        return this.clone(this._state.set('fuelType', fuelType));
    }

    get mileage(): number {
        return this._state.get('mileage');
    }

    setMileage(mileage: number): this {
        return this.clone(this._state.set('mileage', mileage));
    }

    get userCount(): number {
        return this._state.get('userCount');
    }

    setUserCount(userCount: number): this {
        return this.clone(this._state.set('userCount', userCount));
    }

    get documents(): Immutable.List<Document> {
        return this._state.get('documents');
    }

    setDocuments(documents: Immutable.List<Document>): this {
        return this.clone(this._state.set('documents', documents));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get vehicleModel(): ?MasterVehicleModel {
        return this._state.get('vehicleModel');
    }

    setVehicleModel(vehicleModel: ?MasterVehicleModel): this {
        return this.clone(this._state.set('vehicleModel', vehicleModel));
    }

    get vehicleModelId(): ?VehicleModelId {
        return this._state.get('vehicleModelId');
    }

    setVehicleModelId(vehicleModelId: ?VehicleModelId): this {
        return this.clone(this._state.set('vehicleModelId', vehicleModelId));
    }

    get registrationNumber(): string {
        return this._state.get('registrationNumber');
    }

    setRegistrationNumber(registrationNumber: string): this {
        return this.clone(
            this._state.set('registrationNumber', registrationNumber)
        );
    }

    get isActive(): boolean {
        return this._state.get('isActive');
    }

    setIsActive(isActive: boolean): this {
        return this.clone(this._state.set('isActive', isActive));
    }

    get baseLocations(): ?BaseLocations {
        return this._state.get('baseLocations');
    }

    setBaseLocations(baseLocations: ?BaseLocations): this {
        return this.clone(this._state.set('baseLocations', baseLocations));
    }
}
